import { observable, action, set, runInAction, computed } from 'mobx';
import { dispatch } from 'rfx-core';
import Promise from 'bluebird';
import _ from 'lodash';

import BaseUploader from '#/shared/stores/ui/BaseUploader';
import { getChildLogger } from '#/shared/utils/client.logger';

const log = getChildLogger('ui.BulkShiftIssueUploader');

export default class BulkShiftIssueUploader extends BaseUploader {
  constructor({ title = 'ui.BulkShiftIssueUploader' }) {
    super({ title });

    set(this.saveResults, {
      ignored: [],
      invalid: [],
      new: [],
      updated: [],
    });

    return this;
  }

  @observable saveConfirmMessage = '';

  @computed
  get sampleCSVData() {
    return [
      this.knownHeaders,
      [],
      [],
      [],
      [],
      [],
      [],
      ['UUID of shift'],
      [
        'Action Code needs to be one fo the following:',
        'actionSummary: todo',
        'event: todo',
        'isIssueValid: todo',
        'issueCode: todo',
        'issueSource: todo',
        'issueSummary: todo',
        'subIssueCode: todo',
        'partnerId: todo',
        'resolvedAt: todo',
      ],
    ];
  }

  @computed
  get knownHeaders() {
    return [
      'shiftId',
      'actionCode',
      'actionSummary',
      'event',
      'isIssueValid',
      'issueCode',
      'issueSource',
      'issueSummary',
      'subIssueCode',
      'partnerId',
      'resolvedAt',
    ];
  }

  @observable saveButtonText = 'Save';

  @action.bound
  async parse({ rows }) {
    const caseHeaders = _.map(rows[0], _.toLower);
    this.parsedHeaders.replace(this.knownHeaders);
    const indices = _.reduce(
      this.knownHeaders,
      (acc, header) => {
        acc[header] = _.indexOf(caseHeaders, _.toLower(header));
        return acc;
      },
      {},
    );

    this.log.debug('Loaded Table Indices', { indices });

    const validRows = _(rows)
      .map((row, i) => {
        if (!i) return null;

        this.log.debug(row);

        return _.chain(this.knownHeaders)
          .reduce((acc, colKey) => {
            const colIndex = indices[colKey];

            if (colIndex >= 0) {
              acc[colKey] = row[colIndex];
            }
            return acc;
          }, {})
          .value();
      })
      .reject((rowObj) => _.isEmpty(_.omitBy(rowObj, _.isEmpty)))
      .compact()
      .value();

    this.setStatus('Evaluating');

    runInAction(() => {
      this.list.replace(validRows);
    });

    this.setStatus(`Parsed ${this.list.length} Rows to remove`);
  }

  @action.bound
  async processShiftIssue(rowData) {
    const actionCodeList = [
      'callPartnerPickedUp', // Called partner - picked up
      'callPartnerNoAnswer', // Called partner - no answer
      'callStorePickedUp', // Called store - picked up
      'callStoreNoAnswer', // Called store - no answer
      'sendPartnerHome', // Sent partner home
      'other', // Other (provide detail)
    ];
    const eventList = ['create', 'update', 'resolve'];
    const issueCodeList = [
      'billing', // Billing
      'checkInCheckOutIssues', // Check-in/check-out issues
      'criminalBehavior', // Criminal Behavior
      'inappropriateBehaviorTeachable', // Inappropriate behavior - teachable behavior
      'inappropriateBehaviorUnacceptable', // Inappropriate behavior - completely unacceptable behavior
      'lateArrival', // Late Arrival
      'injuryAccommodationRequest', // Injury/Accommodation Request
      'noShow', // No Show
      'noWorkCompletion', // No work completion
      'other', // Other (please describe)
      'partialWorkCompletion', // Partial work completion
      'partnerTurnaway', // Partner Turnaway
      'positiveFeedback', // Positive Feedback
      'scheduleRelatedRequests', // Schedule-related requests
      'shiftAbandonment', // Shift abandonment
      'equipmentDamage', // Equipment damage
      'storeManagerAltercation', // Store manager altercation
      'lowWorkQuality', // Low work quality
    ];
    const issueSourceList = [
      'internalLiveMonitoring', // Internal - Live Monitoring
      'customerFrontInbox', // Customer - Front Inbox
      'customerTextSMS', // Customer - Text / SMS
      'customerDirectCall', // Customer - Direct Call
      'customerPersonalEmail', // Customer - Personal Email
      'partnerZendeskTicket', // Partner - Zendesk Ticket
      'internalLiveShiftSupport', // Internal - Live Shift Support
    ];

    const subIssueCodeList = [
      'planogramNotFollowed', // Planogram (tags) not followed
      'insufficientOutput', // Insufficient output (not enough stocking)
      'backstockNotOrganized', // Backstock not organized &/or tidy
      'poorMerchandisingQuality', // Merchandising quality is poor
      'competitorProductsMerchandised', // Competitor products were merchandised
      'incompleteTLM', // Incomplete TLM (no photos in dashboard)
      'productsDamaged', // Products were damaged
      'productsCordwooding', // Products stacked on each other (cordwooding)
      'partnerLeftEarly', // Partner left early
      'lowQualityWork', // Low quality work
      'dressCodeViolation', // Dress code
      'insufficientTraining', // Insufficient training
      'appNotWorking', // App not working
      'storeNoCodeReceived', // Store did not receive code
      'wrongStoreVisit', // Partner went to wrong store
      'storeClosed', // Store closed
      'equipmentNotWorking', // Equipment not working
      'truckDeliveryMissed', // Truck delivery did not arrive
      'insufficientHoursBudget', // Store has insufficient hours/budget
      'dressCode', // Dress code (duplicate case)
      'shiftDelayedByShiftsmart', // Shift pushed back by Shiftsmart
      'multiplePartnersSent', // Multiple partners sent by Shiftsmart
      'commentsNotClear', // Comments not clear
      'managerRude', // Manager rude
      'noCommentsLogged', // No comments logged
      'noFoodHandlersCard', // No food handlers card
      'noInventory', // No inventory
      'partialBin', // Partial bin
      'partnerDidOtherWork', // Partner did other work
      'partnerIssue', // Partner issue
      'partnerReported', // Partner reported
      'schedulingMistakeByStore', // Scheduling mistake by store
      'storeCooksEarly', // Store cooks early
      'storeDidBinPrep', // Store did bin prep
      'storeNoBinPrep', // Store did not do bin prep
      'storeNoCooking', // Store did not cook
      'tlmOverride', // TLM override
      'tooLateToStart', // Too late to start
      'workAlreadyCompleted', // Work already completed
      'other', // Other
    ];
    // todo typ.
    const row: Record<string, any> = {};
    Object.keys(rowData).forEach((key) => {
      row[key] = _.trim(rowData[key]);
    });
    const {
      shiftId,
      actionCode,
      actionSummary,
      event,
      isIssueValid,
      issueCode,
      issueSource,
      issueSummary,
      subIssueCode,
      partnerId,
      resolvedAt,
    } = row;
    if (!shiftId) {
      throw 'no shift id';
    }
    if (actionCode && !actionCodeList.includes(actionCode)) {
      throw 'invalid action code';
    }
    if (event && !eventList.includes(event)) {
      throw 'invalid event code';
    }
    if (issueCode && !issueCodeList.includes(issueCode)) {
      throw 'invalid issueCode code';
    }
    if (issueSource && !issueSourceList.includes(issueSource)) {
      throw 'invalid issue source';
    }
    if (subIssueCode && !subIssueCodeList.includes(subIssueCode)) {
      throw 'invalid subissue source';
    }
    const user = dispatch('auth.getUser');
    const userId = user?.uuid;
    const createdAt = Date.now();
    const isIssueValidBoolean = /^true$/i.test(isIssueValid);

    const escalation = {
      actionCode,
      actionSummary,
      createdAt,
      event,
      isIssueValid: isIssueValidBoolean,
      issueCode,
      issueSource,
      issueSummary,
      partnerId,
      resolvedAt,
      userId,
    };
    console.log('escalation', escalation, shiftId);
    if (shiftId) {
      const shiftIssue = await dispatch('shifts.update', {
        data: {
          $push: {
            escalations: escalation,
          },
        },
        id: shiftId,
      });
      return shiftIssue;
    }
    return null;
  }

  @action.bound
  async save() {
    this.setStatus('saving');
    this.setSaving(true);

    _.each(this.saveResults, (vals, key) => (this.saveResults[key] = []));

    try {
      const response = await Promise.map(
        this.list,
        async (row) => {
          try {
            const shiftIssue = await this.processShiftIssue(row);
            runInAction(() => {
              this.saveResults.updated.push(shiftIssue);
            });
            return shiftIssue;
          } catch (error) {
            log.debug(error);
            runInAction(() => {
              this.saveResults.invalid.push(_.extend(row, { err: error }));
            });
            return row;
          }
        },
        { concurrency: 20 },
      );

      runInAction(() => {
        this.list.replace(response);
        this.setStatus('saved');
      });
    } catch (err) {
      this.log.error('Failed to update shift issue', err);
      dispatch('ui.snackBar.error', `Sorry, something went wrong, ${err}`);
      this.setStatus('error');
      this.clear();
    }

    this.setSaving(false);
  }

  @action.bound
  clear() {
    super.clear();

    this.list.clear();
  }
}
